import { ActionIcon, Divider, Flex, Text } from '@mantine/core';
import React, { useEffect } from 'react';
import { TemplateSectionCreationData } from 'src/company/templates/types';
import useSplitMarkdownMutation from 'src/company/templates/hooks/useSplitMarkdownMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';

type Props = {
  section: TemplateSectionCreationData;
  onSectionSplit: (sections: TemplateSectionCreationData[]) => void;
  placeholderImages?: MarkdownImage[];
};

const generateMarkdownHeader = (level: number, header: string) => {
  return `\n${'#'.repeat(level)} ${header}\n\n`;
};

const extractHeaderFromMarkdown = (markdown: string | undefined) => {
  if (!markdown) {
    return undefined;
  }

  const headerMatch = markdown.match(/^ {0,3}(#{1,6})\s+(.+)$/m);

  if (!headerMatch) {
    return undefined;
  }

  const [, hashes, headerText] = headerMatch;

  return {
    sectionHeader: headerText.trim(),
    sectionHeaderLevel: hashes.length,
  };
};

export const mergeSectionsIntoOne = (sections: TemplateSectionCreationData[]) => {
  if (!sections.length) {
    throw new Error('mergeSectionsIntoOne requires at least one section');
  }

  const mergedSection = sections.reduce(
    (finalSection, section, index) => {
      if (index > 0) {
        finalSection.templatedMarkdown +=
          generateMarkdownHeader(section.sectionHeaderLevel, section.sectionHeader) +
          section.templatedMarkdown;
        finalSection.populatedMarkdown +=
          generateMarkdownHeader(section.sectionHeaderLevel, section.sectionHeader) +
          section.populatedMarkdown;
      }

      return finalSection;
    },
    { ...sections[0] },
  );

  const sectionWithHeader = sections.find(candidate => candidate.sectionHeader?.trim());

  if (sectionWithHeader) {
    mergedSection.sectionHeader = sectionWithHeader.sectionHeader;
    mergedSection.sectionHeaderLevel = sectionWithHeader.sectionHeaderLevel;
  } else {
    const inferredHeader = extractHeaderFromMarkdown(mergedSection.templatedMarkdown);

    if (inferredHeader) {
      mergedSection.sectionHeader = inferredHeader.sectionHeader;
      mergedSection.sectionHeaderLevel = inferredHeader.sectionHeaderLevel;
    }
  }

  return mergedSection;
};

export default function SplitMarkdownEditor({ section, onSectionSplit, placeholderImages }: Props) {
  const [splitPosition, setSplitPosition] = React.useState<number>(0);
  const { splitMarkdown, sections, isLoading, error } = useSplitMarkdownMutation();

  useEffect(() => {
    setSplitPosition(0);
  }, [section]);

  useEffect(() => {
    const headerMarkdown = generateMarkdownHeader(
      section.sectionHeaderLevel,
      section.sectionHeader,
    );
    splitMarkdown({
      level: 10,
      populatedMarkdown: headerMarkdown + (section.populatedMarkdown || ''),
      templateMarkdown: headerMarkdown + section.templatedMarkdown,
    }).catch(e => {
      notifyAndLogError(
        'Failed to split markdown',
        'An error occurred while splitting the markdown. Please try again.',
        e,
      );
    });
  }, []);

  useEffect(() => {
    if (sections && sections.length >= 2) {
      const first = sections.slice(0, splitPosition + 1);
      const second = sections.slice(splitPosition + 1);

      if (first.length > 0 && second.length > 0) {
        const firstSection = mergeSectionsIntoOne(first);
        const secondSection = mergeSectionsIntoOne(second);
        onSectionSplit([firstSection, secondSection]);
      }
    }
  }, [splitPosition, sections]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  if (!sections) {
    return <Text>Could not split the markdown</Text>;
  }

  if (sections.length < 2) {
    return <Text c="gray.6">This section cannot be split further (no sub-headers found)</Text>;
  }

  const first = sections.slice(0, splitPosition + 1);
  const second = sections.slice(splitPosition + 1);

  const firstSection = mergeSectionsIntoOne(first);
  const secondSection = mergeSectionsIntoOne(second);

  const renderHeaderLabel = (label: string | undefined) => {
    return (
      <Text size="xl" fw={FONT_WEIGHTS.semibold} c="gray.8">
        {label}
      </Text>
    );
  };

  return (
    <Flex
      p="sm"
      bg="gray.0"
      style={{ borderRadius: '8px', minWidth: 0 }}
      direction={'column'}
      gap={'md'}
    >
      <MarkdownEditor
        key={`first-${splitPosition}`}
        label={renderHeaderLabel(firstSection.sectionHeader)}
        defaultValue={firstSection.templatedMarkdown}
        placeholderImages={placeholderImages}
      />
      <Flex align="center" direction="column" gap="xs">
        <ActionIcon
          variant="filled"
          radius={'xl'}
          size={'sm'}
          onClick={() => setSplitPosition(splitPosition - 1)}
          disabled={splitPosition === 0}
        >
          <IconArrowUp style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
        <Divider w="100%" />
        <ActionIcon
          variant="filled"
          radius={'xl'}
          size={'sm'}
          onClick={() => setSplitPosition(splitPosition + 1)}
          disabled={splitPosition === sections.length - 2}
        >
          <IconArrowDown style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
      </Flex>
      <MarkdownEditor
        key={`second-${splitPosition}`}
        label={renderHeaderLabel(secondSection.sectionHeader)}
        defaultValue={secondSection.templatedMarkdown}
        placeholderImages={placeholderImages}
      />
    </Flex>
  );
}
